import { useHttpClient } from "@/vf"
import type { AxiosError } from "axios"
import { isRef, type MaybeRef, ref, unref, watch, type Ref } from "vue"

export interface UseHttpGetReturn<T, E> {
    /** @deprecated, use data instead */
    content: Ref<T | undefined>
    data: Ref<T | undefined>
    error: Ref<E | undefined>
    loading: Ref<boolean>
    statusCode: Ref<number | undefined>
    refresh: () => Promise<void>
}

export function useHttpGet<T, E = any>(
    url: MaybeRef<string>,
    awaitInitialRequest: true,
): Promise<UseHttpGetReturn<T, E>>
export function useHttpGet<T, E = any>(url: MaybeRef<string>, awaitInitialRequest: false): UseHttpGetReturn<T, E>
export function useHttpGet<T, E = any>(url: MaybeRef<string>): Promise<UseHttpGetReturn<T, E>>

export function useHttpGet<T, E = any>(
    url: MaybeRef<string>,
    awaitInitialRequest = true,
): UseHttpGetReturn<T, E> | Promise<UseHttpGetReturn<T, E>> {
    const http = useHttpClient()
    const content = ref<T>()
    const error = ref<E>()
    const loading = ref<boolean>(true)
    const statusCode = ref<number>()

    async function refresh() {
        loading.value = true

        try {
            const response = await (awaitInitialRequest ? http.get : http.getBg)<T>(unref(url))
            content.value = response.data
            statusCode.value = response.status
            error.value = undefined
        } catch (e) {
            const err = e as AxiosError<E>
            error.value = err.response?.data
            statusCode.value = err.response?.status
            content.value = undefined
        }

        loading.value = false
    }

    if (isRef(url)) {
        watch(url, () => refresh())
    }

    if (awaitInitialRequest) {
        return refresh().then(() => ({ content, loading, statusCode, refresh, data: content, error }))
    } else {
        refresh().then()
        return { content, loading, statusCode, refresh, data: content, error }
    }
}
